import {useEffect, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'

import {Modal, useModalContext} from '../../../../_metronic/elements/Modal'
import {useErrorHandler} from '../../../../_metronic/helpers/ErrorHandler'
import {InputText} from '../../../../_metronic/elements/Inputs'
import {openAlert} from '../../../../_metronic/elements/NotificationAlert'
import * as UserService from '../../user/services/UserService'
import * as ResellerService from '../services/ResellerService'
import {useTranslation} from 'react-i18next'
import {formatFixedNumber} from '../../../shared/numberFormat'
import {clearAssignmentRequest, getOrCreateAssignmentRequest} from './assignmentRequestId'
import {normalizePlanetHdResellerContext} from './planetHdResellerContext'
import {PLANET_HD_V1_ENABLED} from '../../../config/featureFlags'

const RESOURCE_LABEL_KEYS = {
  ambients: 'reseller:resources.ambients',
  reports_ndvi_ha: 'reseller:resources.reports',
  planet_hd_ha: 'reseller:resources.planet_hd',
}

const getResourceLabel = (t, resourceId) =>
  t(RESOURCE_LABEL_KEYS[resourceId] || 'reseller:resources.generic')

const ModalHeader = ({user, resourceLabel}) => {
  const {t} = useTranslation()
  return (
    <>
      <div className='modal-header reseller-modal-header'>
        <h5 className='modal-title'>
          {t('reseller:assign_resources.title', {name: user.first_name, resource: resourceLabel})}
        </h5>
      </div>
    </>
  )
}

const ModalFooter = ({disabled, submitting}) => {
  const {t} = useTranslation()
  const modal = useModalContext()

  return (
    <div className='modal-footer reseller-modal-footer'>
      <button type='button' className='btn btn-light' onClick={() => modal.hideModal()}>
        {t('reseller:assign_resources.buttons.cancel')}
      </button>
      <button disabled={submitting || disabled} type='submit' className='btn btn-primary'>
        {t('reseller:assign_resources.buttons.assign')}
      </button>
    </div>
  )
}

const ModalBody = ({submitting, formik, periodUnavailable, remainingHa, resourceLabel}) => {
  const {t} = useTranslation()
  const remainingHaText = formatFixedNumber(remainingHa)
  return (
    <>
      <div className='modal-body reseller-modal-body'>
        <p>
          {t('reseller:assign_resources.form.ha.title', {
            ha: remainingHaText,
            resource: resourceLabel,
          })}
        </p>
        {periodUnavailable && (
          <div className='alert alert-warning' role='alert'>
            {t('reseller:assign_resources.planet_hd_period_unavailable.message')}
          </div>
        )}
        <InputText
          disabled={submitting || periodUnavailable}
          formik={formik}
          name='ha'
          inputProps={{
            placeholder: t('reseller:assign_resources.form.ha.placeholder'),
            type: 'number',
          }}
        />
      </div>
    </>
  )
}

const AssignResource = ({open, setOpen, user, loadUsers}) => {
  const {errorHandler} = useErrorHandler()
  const [loading, setLoading] = useState(true)
  const [plan, setPlan] = useState({})
  const [usage, setUsage] = useState({})
  const [planetHdContext, setPlanetHdContext] = useState(() =>
    normalizePlanetHdResellerContext(null)
  )
  const [submitting, setSubmitting] = useState(false)
  const {t} = useTranslation()
  const schema = Yup.object().shape({
    ha: Yup.number()
      .required(t('reseller:assign_resources.form.ha.errors.required'))
      .test(
        'Is positive?',
        t('reseller:assign_resources.form.ha.errors.min'),
        (value) => value > 0
      ),
  })
  const resourceLabel = useMemo(() => getResourceLabel(t, open), [open, t])
  const isPlanetHdAssignment = open === 'planet_hd_ha'
  const periodUnavailable = isPlanetHdAssignment && !planetHdContext.canAssign
  const remainingHa = useMemo(() => {
    if (
      open !== 'ambients' &&
      open !== 'reports_ndvi_ha' &&
      (open !== 'planet_hd_ha' || !PLANET_HD_V1_ENABLED)
    ) {
      return 0
    }

    if (open === 'planet_hd_ha') {
      return planetHdContext.reseller.availableHa
    }

    if (!Array.isArray(plan.resources) || !Array.isArray(usage.usages)) {
      return 0
    }

    let reseller_resource
    if (open === 'ambients') {
      reseller_resource = 'reseller_ambients_ha'
    } else if (open === 'reports_ndvi_ha') {
      reseller_resource = 'reseller_reports_ndvi_ha'
    }

    const ambients =
      plan.resources.filter((e) => e.resource_id === reseller_resource)[0]?.quantity || 0
    let usageHa = usage.usages.filter((e) => e.resource === reseller_resource)

    if (usageHa.length < 1) {
      usageHa = 0
    } else {
      usageHa = usageHa[0].usage
    }

    return ambients - usageHa
  }, [open, plan, planetHdContext.reseller.availableHa, usage])
  const load = async () => {
    setLoading(true)
    const [res_usage, res_plan, res_planet_hd] = await Promise.all([
      errorHandler(UserService.companyUsage()),
      errorHandler(UserService.currentPlan()),
      open === 'planet_hd_ha'
        ? errorHandler(ResellerService.getPlanetHdContext(), [404, 409, 503])
        : Promise.resolve(null),
    ])

    if (res_plan.ok) {
      setPlan(res_plan.data.data)
    }

    if (res_usage.ok) {
      setUsage(res_usage.data.data)
    }

    setPlanetHdContext(
      normalizePlanetHdResellerContext(res_planet_hd?.ok ? res_planet_hd.data : null)
    )

    setLoading(false)
  }
  const formik = useFormik({
    initialValues: {
      ha: '',
    },
    validationSchema: schema,
    onSubmit: (values, {resetForm}) => {
      if (open === 'planet_hd_ha' && !PLANET_HD_V1_ENABLED) {
        return
      }

      if (open === 'planet_hd_ha' && !planetHdContext.canAssign) {
        openAlert(
          'danger',
          t('reseller:assign_resources.planet_hd_period_unavailable.title'),
          t('reseller:assign_resources.planet_hd_period_unavailable.message')
        )
        return
      }

      setSubmitting(true)
      const base = {
        ha: 0,
        ha_reports_ndvi: 0,
        ha_hd: 0,
      }

      if (open === 'ambients') {
        base['ha'] = values.ha
      } else if (open === 'reports_ndvi_ha') {
        base['ha_reports_ndvi'] = values.ha
      } else if (open !== 'planet_hd_ha') {
        setSubmitting(false)
        return
      }

      const assignmentRequest = getOrCreateAssignmentRequest({
        companyId: user.company_id,
        resourceId: open,
        quantity: values.ha,
      })
      const assignment =
        open === 'planet_hd_ha'
          ? ResellerService.assignPlanetHd({
              company_id: user.company_id,
              assigned_hd_ha: Number(values.ha),
              expected_provider_period_id: planetHdContext.providerPeriod.providerPeriodId,
              idempotency_key: assignmentRequest.requestId,
            })
          : ResellerService.assignAmbient({
              company_id: user.company_id,
              idempotency_key: assignmentRequest.requestId,
              ...base,
            })

      errorHandler(assignment, [402]).then((res) => {
        if (res.ok) {
          clearAssignmentRequest(assignmentRequest.storageKey)
          loadUsers()
          resetForm()
          openAlert(
            'success',
            t('reseller:assign_resources.success.title'),
            t('reseller:assign_resources.success.message')
          )
          setOpen(false)
        } else if (res.error_type === 'need_payment') {
          openAlert(
            'danger',
            t('reseller:assign_resources.not_enough_ha.title'),
            t('reseller:assign_resources.not_enough_ha.message')
          )
        }

        setSubmitting(false)
      })
    },
  })

  useEffect(() => {
    if (open) {
      load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (loading || !user || (open === 'planet_hd_ha' && !PLANET_HD_V1_ENABLED)) {
    return null
  }

  return (
    <>
      <Modal open={open} setOpen={setOpen}>
        <div className='modal-dialog modal-dialog-centered reseller-modal-dialog'>
          <div className='modal-content reseller-modal-content'>
            <form onSubmit={formik.handleSubmit} className='form'>
              <ModalHeader user={user} resourceLabel={resourceLabel} />
              <ModalBody
                submitting={submitting}
                formik={formik}
                periodUnavailable={periodUnavailable}
                remainingHa={remainingHa}
                resourceLabel={resourceLabel}
              />
              <ModalFooter disabled={periodUnavailable} submitting={submitting} />
            </form>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AssignResource
